import React, { useState, useRef, useEffect } from 'react';
import { createTheme, ThemeProvider, CssBaseline, AppBar, Toolbar, Typography, Button, Container, Box, Stack, TextField, Checkbox, FormControlLabel, IconButton, Drawer, List, ListItem, ListItemText, useScrollTrigger, Slide, Link as MuiLink } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Link as ScrollLink } from 'react-scroll';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import { content } from './content';

const theme = createTheme({
    palette: {
        primary: { main: '#b90010' },
        secondary: { main: '#1a1a1a' },
        background: { default: '#f4f4f4', paper: '#ffffff' },
        text: { primary: '#1a1a1a', secondary: '#666666' }
    },
    typography: {
        fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontWeight: 700 },
        h2: { fontWeight: 600 },
        h3: { fontWeight: 600 },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { textTransform: 'none', fontWeight: 600, padding: '10px 24px' },
            },
        },
    },
});


interface NavbarProps {
    lang: 'SK' | 'EN';
    setLang: React.Dispatch<React.SetStateAction<'SK' | 'EN'>>;
    t: any;
}

const Navbar = ({ lang, setLang, t }: NavbarProps) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const trigger = useScrollTrigger();

    const navItems = [
        { id: 'about', label: t.nav.about },
        { id: 'services', label: t.nav.services },
        { id: 'vehicles', label: t.nav.vehicles },
        { id: 'contact', label: t.nav.contact },
    ];

    return (
        <>
            <Slide appear={false} direction="down" in={!trigger}>
                <AppBar position="fixed" sx={{ bgcolor: 'rgba(0,0,0,0.85)', boxShadow: trigger ? 3 : 0, transition: '0.3s', px: { xs: 0, md: 0 } }}>
                    <Container maxWidth="xl" sx={{ px: { xs: 1, md: 3 } }}>
                        <Toolbar disableGutters sx={{ height: { xs: 56, md: trigger ? 64 : 90 }, minHeight: { xs: 56, md: trigger ? 64 : 90 }, transition: '0.3s', justifyContent: 'space-between', px: { xs: 0, md: 0 } }}>
                            {/* Logo */}
                            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: '#fff', cursor:'pointer', fontSize: { xs: '1.2rem', md: '2rem' }, pl: { xs: 1, md: 0 } }}>
                                <ScrollLink to="hero" smooth={true}>ANTHONY<span style={{color:'#b90010'}}>TRANSPORT</span></ScrollLink>
                            </Typography>
                            {/* Desktop Menu - Flexbox Row */}
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                                {navItems.map((item) => (
                                    <ScrollLink key={item.id} to={item.id} smooth={true} offset={-70} duration={500}>
                                        <Button sx={{ color: '#fff', '&:hover': { color: '#b90010' }, fontSize: { xs: '0.9rem', md: '1rem' } }}>{item.label}</Button>
                                    </ScrollLink>
                                ))}
                                <Stack direction="row" spacing={1} ml={2}>
                                    <Button size="small" variant={lang === 'SK' ? 'contained' : 'text'} onClick={() => setLang('SK')} sx={{ minWidth: 'auto', color: '#fff', fontSize: { xs: '0.9rem', md: '1rem' } }}>SK</Button>
                                    <Button size="small" variant={lang === 'EN' ? 'contained' : 'text'} onClick={() => setLang('EN')} sx={{ minWidth: 'auto', color: '#fff', fontSize: { xs: '0.9rem', md: '1rem' } }}>EN</Button>
                                </Stack>
                            </Stack>
                            {/* Mobile Menu Icon */}
                            <IconButton color="inherit" onClick={() => setMobileOpen(!mobileOpen)} sx={{ display: { md: 'none' }, mr: { xs: 1, md: 0 } }}>
                                <MenuIcon fontSize="large" />
                            </IconButton>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Slide>
            {/* Mobile Drawer */}
            <Drawer variant="temporary" anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
                <Box sx={{ width: 250, pt: 4, px: 2 }}>
                    <List>
                        {navItems.map((item) => (
                            <ListItem key={item.id} sx={{ py: 1 }}>
                                <ScrollLink to={item.id} smooth={true} offset={-70} onClick={() => setMobileOpen(false)}>
                                    <ListItemText primary={item.label} sx={{ textAlign: 'center', fontSize: '1.1rem' }} />
                                </ScrollLink>
                            </ListItem>
                        ))}
                        <ListItem sx={{ justifyContent: 'center', gap: 2, mt: 2 }}>
                            <Button variant={lang === 'SK' ? 'contained' : 'outlined'} onClick={() => setLang('SK')} sx={{ minWidth: 40 }}>SK</Button>
                            <Button variant={lang === 'EN' ? 'contained' : 'outlined'} onClick={() => setLang('EN')} sx={{ minWidth: 40 }}>EN</Button>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

// 2. Hero Section
interface SectionProps {
    t: any;
}

const Hero: React.FC<SectionProps> = ({ t }) => (
    <Box id="hero" sx={{
        height: { xs: '70vh', md: '100vh' },
        minHeight: { xs: 400, md: 600 },
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(/assets/kamionik.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: '#fff',
        px: { xs: 2, md: 0 }
    }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 0 } }}>
            <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <Typography variant="h2" component="h1" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                    {t.hero.title.split(' ').map((word: string, i: number) => i === 0 ? <span key={i} style={{color: '#b90010'}}>{word} </span> : word + ' ')}
                </Typography>
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 300, fontSize: { xs: '1.1rem', md: '1.5rem' } }}>{t.hero.subtitle}</Typography>
                <ScrollLink to="contact" smooth={true} offset={-70}>
                    <Button variant="contained" size="large" color="primary" sx={{ borderRadius: 50, px: 5, py: 1.5, fontSize: { xs: '1rem', md: '1.2rem' } }}>{t.nav.contact}</Button>
                </ScrollLink>
            </motion.div>
        </Container>
    </Box>
);

// 3. About Section (Flex Layout)
const About: React.FC<SectionProps> = ({ t }) => (
    <Box id="about" sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' }, // Stĺpec na mobile, riadok na desktope
                alignItems: 'center',
                gap: 6
            }}>
                {/* Obrázok - Flex item 1 */}
                <Box sx={{ flex: 1, width: '100%', height: 400, bgcolor: '#eee', borderRadius: 4, overflow:'hidden', position: 'relative' }}>
                    <img src="https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&w=800&q=80" alt="Nákladné auto - ilustrácia logistika" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                </Box>
                {/* Text - Flex item 2 */}
                <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="primary" sx={{ letterSpacing: 3, fontWeight: 'bold' }}>ANTHONY TRANSPORT</Typography>
                    <Typography variant="h3" sx={{ mb: 3 }}>{t.about.title}</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>{t.about.text}</Typography>
                </Box>
            </Box>
        </Container>
    </Box>
);

// 4. Services Section (Flex Layout + Wrap)
const Services: React.FC<SectionProps> = ({ t }) => (
    <Box id="services" sx={{ py: 10, bgcolor: '#f9f9f9' }}>
        <Container maxWidth="lg">
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', md: 'row' },
                gap: 6
            }}>
                {/* Text a zoznam krajín */}
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h3" sx={{ mb: 3 }}>{t.services.title}</Typography>
                    <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>{t.services.text}</Typography>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mb: 4 }}>{t.services.subText}</Typography>

                    {/* Zoznam krajín - Flex Wrap namiesto Gridu */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {t.services.countries.map((country: string) => (
                            <Box key={country} sx={{
                                p: 2,
                                bgcolor: '#fff',
                                borderRadius: 2,
                                boxShadow: 1,
                                flexBasis: { xs: '45%', sm: '30%' }, // Nastavenie šírky položiek
                                flexGrow: 1,
                                textAlign: 'center'
                            }}>
                                <Typography variant="button">{country}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                {/* Mapa */}
                <Box sx={{ flex: 1 }}>
                    <img src='/assets/europe.jpg' alt="Mapa Európy - krajiny prepravy Anthony Transport" style={{ width: '100%', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                </Box>
            </Box>
        </Container>
    </Box>
);

// 5. Vehicles Section
const Vehicles: React.FC<SectionProps> = ({ t }) => (
    <Box id="vehicles" sx={{ py: 10, bgcolor: '#111', color: '#fff' }}>
        <Container maxWidth="lg">
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                gap: 6
            }}>
                <Box sx={{ flex: 1 }}>
                    <img src='/assets/tahac.svg' alt="Volvo FH-500 ťahač - vozový park Anthony Transport" style={{ width: '100%', filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.1))' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h3" color="primary" sx={{ mb: 3 }}>{t.vehicles.title}</Typography>
                    <Typography variant="body1" sx={{ mb: 2, color: '#ccc' }}>{t.vehicles.text1}</Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2, color: '#fff' }}>
                        {t.vehicles.list.map((item: string, i: number) => (
                            <li key={i}><Typography variant="body1">{item}</Typography></li>
                        ))}
                    </Box>
                    <Typography variant="body1" sx={{ color: '#ccc' }}>{t.vehicles.text2}</Typography>
                </Box>
            </Box>
        </Container>
    </Box>
);

// 6. Contact Section (Flexbox Form)
interface ContactProps extends SectionProps {
    lang: string;
}

const Contact: React.FC<ContactProps> = ({ t }) => {
    const formRef = useRef<HTMLFormElement | null>(null);
    const [status, setStatus] = useState('');
    const [formValues, setFormValues] = useState({
        name: '',
        email: '',
        mobile: '',
        gdpr: false
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const isFormValid = () => {
        return (
            formValues.name.trim() !== '' &&
            formValues.email.trim() !== '' &&
            formValues.mobile.trim() !== '' &&
            formValues.gdpr
        );
    };

    const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!formRef.current) return;
        if (!isFormValid()) return;
        emailjs.sendForm('service_d0idala', 'template_w23atba', formRef.current, 'dEMCB0smYDXE9HT-6')
            .then(() => {
                setStatus('Sent!');
                (e.target as HTMLFormElement).reset();
                setFormValues({ name: '', email: '', mobile: '', gdpr: false });
            }, () => setStatus('Error'));
    };

    return (
        <Box id="contact" sx={{ py: 10, bgcolor: 'background.paper' }}>
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 8 }}>

                    {/* Info Side */}
                    <Box sx={{ flex: { xs: 'none', md: '0 0 40%' } }}>
                        <Typography variant="h3" sx={{ mb: 4 }}>{t.contact.title}</Typography>

                        <Stack spacing={4}>
                            <Box>
                                <Typography variant="h6" color="primary">{t.contact.person}</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>Mgr. Antónia Janiková</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                                    <PhoneIcon sx={{ mr: 1, fontSize: 20 }} /> +421 905 885 694
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="h6" color="primary">{t.contact.office}</Typography>
                                <Typography variant="body1" sx={{ mt: 1 }}>Nižný Čaj 81</Typography>
                                <Typography variant="body1">044 16 Nižný Čaj</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                                    <LocationOnIcon sx={{ mr: 1, fontSize: 20 }} /> Slovakia
                                </Box>
                            </Box>
                        </Stack>

                        <Box sx={{ height: 250, width: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: 1, mt: 4 }}>
                            <iframe title="map" width="100%" height="100%" frameBorder="0" style={{ border: 0 }} src="https://maps.google.com/maps?q=Ni%C5%BEn%C3%BD%20%C4%8Caj%2081%20044%2016%20Slovakia&z=15&output=embed" allowFullScreen />
                        </Box>
                    </Box>

                    {/* Form Side - Using Stack for layout */}
                    <Box sx={{ flex: 1, p: 4, bgcolor: '#f9f9f9', borderRadius: 4 }}>
                        <form ref={formRef} onSubmit={sendEmail}>
                            <Stack spacing={3}>
                                <Typography variant="h4">{t.contact.formTitle}</Typography>

                                <TextField fullWidth label={t.contact.labels.name} name="name" variant="outlined" required value={formValues.name} onChange={handleChange} />

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <TextField fullWidth label={t.contact.labels.email} name="email" type="email" variant="outlined" required value={formValues.email} onChange={handleChange} />
                                    <TextField fullWidth label={t.contact.labels.phone} name="mobile" variant="outlined" required value={formValues.mobile} onChange={handleChange} />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <TextField fullWidth label={t.contact.labels.pickupCity} name="cityDep" variant="outlined" onChange={handleChange} />
                                    <TextField fullWidth label={t.contact.labels.pickupAddr} name="addDep" variant="outlined" onChange={handleChange} />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <TextField fullWidth label={t.contact.labels.deliveryCity} name="cityAr" variant="outlined" onChange={handleChange} />
                                    <TextField fullWidth label={t.contact.labels.deliveryAddr} name="addArr" variant="outlined" onChange={handleChange} />
                                </Box>

                                <TextField fullWidth label={t.contact.labels.message} name="message" multiline rows={4} variant="outlined" onChange={handleChange} />

                                <FormControlLabel control={<Checkbox name="gdpr" checked={formValues.gdpr} onChange={handleChange} required />} label={
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <Typography variant="body2" component="span">{t.contact.labels.gdpr}</Typography>
                                    </Box>
                                } />

                                <Button type="submit" variant="contained" size="large" fullWidth sx={{ py: 1.5 }} disabled={!isFormValid()}>
                                    {status === 'Sent!' ? 'OK' : t.contact.labels.send}
                                </Button>
                            </Stack>
                        </form>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

// 7. Footer
interface FooterProps {
    t: any;
}

const Footer: React.FC<FooterProps> = ({ t }) => (
    <Box sx={{ bgcolor: '#000', color: '#fff', py: 4 }}>
        <Container maxWidth="lg">
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
            }}>
                <Typography variant="h6">{t.footer.copyright}</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
                    {t.footer.links.map((link: string, i: number) => (
                        <MuiLink key={i} href="#" underline="hover" sx={{ color: '#ccc' }}>
                            {link}
                        </MuiLink>
                    ))}
                </Stack>
            </Box>
        </Container>
    </Box>
);

// --- MAIN APP ---
const App = () => {
    const [lang, setLang] = useState<'SK' | 'EN'>('SK');
    const t = content[lang as 'SK' | 'EN'];

    useEffect(() => {
        document.title = lang === 'SK'
            ? 'Anthony Transport - Medzinárodná a vnútroštátna preprava'
            : 'Anthony Transport - International and Domestic Transport';
    }, [lang]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Navbar lang={lang} setLang={setLang} t={t} />
            <Hero t={t} />
            <About t={t} />
            <Services t={t} />
            <Vehicles t={t} />
            <Contact t={t} lang={lang} />
            <Footer t={t} />
        </ThemeProvider>
    );
};

export default App;
